import OutboundIcon from '@mui/icons-material/CallMade';
import InboundIcon from '@mui/icons-material/CallReceived';
import MissedIcon from '@mui/icons-material/CallMissed';
import PhoneIcon from '@mui/icons-material/Phone';
import ClearIcon from '@mui/icons-material/DeleteSweep';
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import moment from 'moment';
import React from 'react';

import { CallHistoryEntry } from '../../../@types/connect.type';
import { formatDuration, formatForDisplay } from '../../../utils/phone-format';
import { useConnect } from '../ConnectProvider';

const directionLabel = (entry: CallHistoryEntry): string =>
  entry.direction === 'inbound' ? 'Inbound' : 'Outbound';

const renderDirectionIcon = (entry: CallHistoryEntry) => {
  if (entry.outcome === 'missed')
    return <MissedIcon fontSize="small" sx={{ color: 'error.main' }} />;
  return entry.direction === 'inbound' ? (
    <InboundIcon fontSize="small" sx={{ color: 'success.main' }} />
  ) : (
    <OutboundIcon fontSize="small" sx={{ color: 'primary.main' }} />
  );
};

export const CallHistoryPanel: React.FC = () => {
  const { history, dial, clearCallHistory, initState } = useConnect();
  const isReady = initState.status === 'ready';

  if (history.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No calls yet. History is kept in this browser only.
        </Typography>
      </Box>
    );
  }

  const onCallback = async (entry: CallHistoryEntry) => {
    if (!entry.phoneNumber) return;
    try {
      await dial(entry.phoneNumber);
    } catch {
      // Errors surface in the dial panel via context state — this list
      // doesn't have its own error slot.
    }
  };

  return (
    <Stack sx={{ height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <Tooltip title="Clear local history">
          <Button size="small" startIcon={<ClearIcon />} onClick={clearCallHistory}>
            Clear
          </Button>
        </Tooltip>
      </Box>
      <List dense sx={{ flex: 1, overflowY: 'auto', py: 0 }}>
        {history.map((entry) => (
          <ListItem
            key={entry.id}
            divider
            secondaryAction={
              entry.phoneNumber && (
                <Tooltip title={`Call back ${formatForDisplay(entry.phoneNumber)}`}>
                  <span>
                    <IconButton
                      edge="end"
                      onClick={() => onCallback(entry)}
                      disabled={!isReady}
                      aria-label={`Call back ${formatForDisplay(entry.phoneNumber)}`}
                    >
                      <PhoneIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )
            }
          >
            <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
              {renderDirectionIcon(entry)}
            </Box>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                    {formatForDisplay(entry.phoneNumber)}
                  </Typography>
                  {entry.outcome === 'missed' && (
                    <Chip
                      label="Missed"
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ height: 18 }}
                    />
                  )}
                </Box>
              }
              secondary={
                <Typography component="span" variant="caption" color="text.secondary">
                  {directionLabel(entry)} · {moment(entry.startedAt).fromNow()} ·{' '}
                  {formatDuration(entry.durationSeconds)}
                </Typography>
              }
            />
          </ListItem>
        ))}
      </List>
    </Stack>
  );
};
